// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {IconWdt} from '@deere/icons';
import {COURSE} from 'Common/constants/membership-type';
import {MINUTES_PER_HOUR} from 'Ui/constants/time-constants';
import {
    BY_JOB_CATEGORY,
    BY_JOB_TEMPLATE,
    BY_TIME,
    BY_USER,
    JOB_CATEGORIES_BY_TIME,
    JOB_TEMPLATES_BY_TIME,
    USERS_BY_TIME
} from 'OnLabor/report/constants/labor-report';
import {exportLaborWorkboardsReport} from 'Services/excel-service';
import {formatNumber, getFormattedCurrency} from 'Utils/unit-conversion-utils';
import {groupBy, sortBy} from 'lodash';
import {dataTableFormatTime} from 'Utils/time-utils';
import {useNavBarActions} from 'Ui/react-hooks/use-navbar-actions';
import {
    VIEW_FINANCIAL_DATA,
    VIEW_WORKBOARD_SUMMARY_DATA
} from 'Common/constants/business-activities';
import {PRO_GOLF_LABOR_REPORTS_1} from 'Common/constants/isg-toggles';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {connect} from 'react-redux';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {GridTreeDataGroupingCell} from '@deere/isg.component-library';
import DataGridWrapper from '../../common/data-table/data-grid-wrapper';
import {v4} from 'uuid';
import LaborReportExportDialog from 'OnLabor/report/labor-report-export-dialog';
import {getLaborWorkboardsExportContent} from 'Utils/labor-report-export-utils';
import moment from 'moment';

function getNameForHeader(secondarySelector, translations) {
    switch (secondarySelector) {
        case BY_JOB_CATEGORY:
        case JOB_CATEGORIES_BY_TIME:
            return translations.ONLINK_JOB_CATEGORY;
        case BY_JOB_TEMPLATE:
        case JOB_TEMPLATES_BY_TIME:
            return translations.jobs_job;
        case BY_USER:
        case USERS_BY_TIME:
            return translations.ONLINK_EMPLOYEE;
        default:
            return secondarySelector;
    }
}

function createColumnOrDefault(shouldCreate, column) {
    return shouldCreate ? [column] : [];
}

function formatMinutesToHoursNumber(value) {
    return formatNumber(value / MINUTES_PER_HOUR, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

function getColumns({
    groupKey,
    membership,
    secondarySelector,
    shouldPivot,
    timeScale,
    translations
}) {
    const currencyPreferenceByToggles = {
        currencyPreference: membership.currencyPreference
    };

    return [
        ...createColumnOrDefault(secondarySelector !== BY_TIME && !shouldPivot, {
            headerName: getNameForHeader(secondarySelector, translations),
            field: 'name',
            minWidth: 200,
            flex: 1
        }),
        ...createColumnOrDefault(groupKey === 'dateSelect', {
            headerName: translations.DATE,
            field: 'dateSelect',
            minWidth: 150,
            flex: 1,
            valueGetter: (value, row) => {
                if (!value) {
                    return '';
                }

                if (shouldPivot && row.path?.length === 1) {
                    const formattedDate = dataTableFormatTime(value, timeScale);

                    return `${formattedDate} ${translations.ONLINK_TOTAL}`;
                }

                return dataTableFormatTime(value, row.dateScale);
            }
        }),
        {
            headerName: translations.ONLINK_EST_LABOR_HOURS,
            field: 'estDuration',
            minWidth: 150,
            flex: 1,
            type: 'number',
            valueFormatter: (value) => formatMinutesToHoursNumber(value)
        },
        {
            headerName: translations.ONLINK_ESTIMATED_COST,
            field: 'estLaborCost',
            minWidth: 150,
            flex: 1,
            type: 'number',
            valueFormatter: (value) => getFormattedCurrency(value, currencyPreferenceByToggles),
            requiredMyJdPermissions: VIEW_FINANCIAL_DATA
        },
        {
            headerName: translations.ONLINK_ACTUAL_HOURS,
            field: 'actualDuration',
            minWidth: 150,
            flex: 1,
            type: 'number',
            valueFormatter: (value) => formatMinutesToHoursNumber(value)
        },
        {
            headerName: translations.ONLINK_ACTUAL_COST,
            field: 'laborCost',
            minWidth: 150,
            flex: 1,
            type: 'number',
            valueFormatter: (value) => getFormattedCurrency(value, currencyPreferenceByToggles),
            requiredMyJdPermissions: VIEW_FINANCIAL_DATA
        },
        {
            headerName: translations.ONLINK_OVERTIME_HOURS,
            field: 'overtimeDuration',
            minWidth: 150,
            flex: 1,
            type: 'number',
            valueFormatter: (value) => formatMinutesToHoursNumber(value)
        },
        {
            headerName: translations.ONLINK_OVERTIME_COST,
            field: 'overtimeCost',
            minWidth: 150,
            flex: 1,
            type: 'number',
            valueFormatter: (value) => getFormattedCurrency(value, currencyPreferenceByToggles),
            requiredMyJdPermissions: VIEW_FINANCIAL_DATA
        }
    ];
}

function getAggregatedValues(dataItems) {
    return dataItems.reduce((acc, item) => {
        const {
            actualDuration,
            estDuration,
            estLaborCost,
            laborCost,
            overtimeDuration,
            overtimeCost
        } = item;

        acc.actualDuration += actualDuration;
        acc.laborCost += laborCost;
        acc.estDuration += estDuration;
        acc.estLaborCost += estLaborCost;
        acc.overtimeDuration += overtimeDuration;
        acc.overtimeCost += overtimeCost;

        return acc;
    }, {
        actualDuration: 0,
        estDuration: 0,
        estLaborCost: 0,
        laborCost: 0,
        overtimeDuration: 0,
        overtimeCost: 0
    });
}

function getGroupedDataByNameAndDateSelect(groupedDataByName) {
    return Object.keys(groupedDataByName).reduce((groupedDataByNameAndDate, name) => {
        const items = groupedDataByName[name];

        groupedDataByNameAndDate[name] = groupBy(items, 'dateSelect');

        return groupedDataByNameAndDate;
    }, {});
}

function getGroupedData(chartDataForSelector, groupKey, secondarySelector) {
    if (secondarySelector !== BY_TIME) {
        const groupedDataByName = groupBy(chartDataForSelector, 'name');

        return groupKey === 'dateSelect' ?
            getGroupedDataByNameAndDateSelect(groupedDataByName) :
            groupedDataByName;
    }

    return groupBy(chartDataForSelector, 'dateSelect');
}

function getDataGridRowsData(chartDataForSelector, groupKey, secondarySelector, shouldPivot) {
    const groupedData = getGroupedData(chartDataForSelector, groupKey, secondarySelector);

    return Object.keys(groupedData).reduce((rows, nameKey) => {
        const dataItems = groupedData[nameKey];

        if (Array.isArray(dataItems)) {
            const aggregatedValues = getAggregatedValues(dataItems);
            const [{
                dateScale,
                dateSelect,
                name
            }] = dataItems;

            const rowId = v4();

            rows.push({
                ...aggregatedValues,
                id: rowId,
                path: [rowId],
                dateScale,
                dateSelect,
                name
            });
        } else {
            const childItems = Object.keys(dataItems).map((dateSelectKey) => dataItems[dateSelectKey][0]);

            if (shouldPivot) {
                const parentAggregated = getAggregatedValues(childItems);

                rows.push({
                    ...parentAggregated,
                    id: v4(),
                    path: [childItems[0].name],
                    dateSelect: childItems[0].dateSelect,
                    name: childItems[0].name
                });
            }

            childItems.forEach((item) => {
                const rowId = v4();

                rows.push({
                    id: rowId,
                    path: shouldPivot ? [item.name, item.dateSelect] : [rowId],
                    actualDuration: item.actualDuration,
                    dateScale: item.dateScale,
                    dateSelect: item.dateSelect,
                    estDuration: item.estDuration,
                    estLaborCost: item.estLaborCost,
                    laborCost: item.laborCost,
                    name: item.name,
                    overtimeDuration: item.overtimeDuration,
                    overtimeCost: item.overtimeCost
                });
            });
        }

        return rows;
    }, []);
}

function getFormattedData(secondarySelector, rowsData, translations, membership) {
    const columnHeaderName = getNameForHeader(secondarySelector, translations);
    const title = secondarySelector === BY_JOB_CATEGORY ? 'Job Categories' : 'Jobs';

    const formattedData = rowsData.map((row) => {
        return {
            ...row,
            actualDuration: row.actualDuration / MINUTES_PER_HOUR,
            estDuration: row.estDuration / MINUTES_PER_HOUR,
            overtimeDuration: row.overtimeDuration / MINUTES_PER_HOUR
        };
    });

    return {
        columnHeaderName,
        currencyPreference: membership.currencyPreference,
        title,
        workboardsReport: sortBy(formattedData, 'name')
    };
}

function useLaborWorkboardExport({
    addToast,
    dateSelector,
    endDate,
    groupKey,
    hasFinancialDataPermissions,
    membership,
    rowsData,
    secondarySelector,
    shouldPivot,
    isExportPreviewEnabled,
    startDate,
    timeScale,
    translations
}) {
    const [showExportDialog, setShowExportDialog] = React.useState(false);
    const exportArgs = React.useMemo(() => {
        const exportRows = shouldPivot ? rowsData.filter((row) => row.path?.length > 1) : rowsData;

        return {
            ...getFormattedData(secondarySelector, exportRows, translations, membership),
            dateSelector,
            startDate,
            endDate,
            includeDate: groupKey === 'dateSelect',
            formatNumbers: true,
            timeScale,
            translations,
            hasViewFinancialDataPermission: hasFinancialDataPermissions,
            addToast
        };
    }, [shouldPivot, rowsData, secondarySelector, translations, membership, dateSelector, startDate, endDate, groupKey, timeScale, hasFinancialDataPermissions, addToast]);
    const exportContent = React.useMemo(() => getLaborWorkboardsExportContent(exportArgs), [exportArgs]);
    const selectedTimeframe = startDate && endDate ?
        `${moment(startDate, 'YYYYMMDD').format('MMM D, YYYY')} - ${moment(endDate, 'YYYYMMDD').format('MMM D, YYYY')}` :
        moment(dateSelector, 'YYYYMMDD').format('MMM D, YYYY');

    const handleClose = () => setShowExportDialog(false);
    const handleDownload = React.useCallback(async () => {
        await exportLaborWorkboardsReport(exportArgs);
        setShowExportDialog(false);
    }, [exportArgs]);
    const handleOpen = React.useCallback(async () => {
        if (isExportPreviewEnabled) {
            setShowExportDialog(true);
        } else {
            await handleDownload();
        }
    }, [handleDownload, isExportPreviewEnabled]);

    return {
        exportContent,
        handleClose,
        handleDownload,
        handleOpen,
        selectedTimeframe,
        showExportDialog
    };
}

function WorkboardDataTable(props) {
    const {
        laborReportData: chartDataForSelector,
        dateSelector,
        endDate,
        isTimeAxis,
        membership,
        secondarySelector,
        startDate,
        timeScale,
        translations,
        isMigrated,
        myJdPermissions,
        isExportPreviewEnabled
    } = props;

    const {addToast} = useToast();
    const groupKey = isTimeAxis ? 'dateSelect' : 'name';
    const isCourse = membership.membershipType === COURSE;
    const shouldPivot = timeScale !== 'day' && secondarySelector !== BY_TIME && isTimeAxis && isCourse;

    let rowsData = chartDataForSelector;

    if (isCourse) {
        rowsData = getDataGridRowsData(chartDataForSelector, groupKey, secondarySelector, shouldPivot);
    }

    const userAuth = {
        isMigrated,
        myJdPermissions
    };

    const myJdPermissionMap = React.useMemo(() => {
        return {
            hasFinancialDataPermissions: isAuthorized({
                myJdPermissions: VIEW_FINANCIAL_DATA
            }, userAuth),
            hasExportDataPermission: isAuthorized({
                myJdPermissions: VIEW_WORKBOARD_SUMMARY_DATA
            }, userAuth)
        };
    }, [isMigrated, myJdPermissions]);
    const {
        exportContent,
        handleClose,
        handleDownload,
        handleOpen,
        selectedTimeframe,
        showExportDialog
    } = useLaborWorkboardExport({
        addToast,
        dateSelector,
        endDate,
        groupKey,
        hasFinancialDataPermissions: myJdPermissionMap.hasFinancialDataPermissions,
        membership,
        rowsData,
        secondarySelector,
        shouldPivot,
        isExportPreviewEnabled,
        startDate,
        timeScale,
        translations
    });

    useNavBarActions(myJdPermissionMap.hasExportDataPermission ? [{
        Icon: <IconWdt/>,
        onClick: handleOpen,
        title: 'EXPORT',
        variant: 'primary'
    }] : []);

    return (
        <>
            <div className={'ops-center-table-container'}>
                <DataGridWrapper
                    columns={getColumns({
                        groupKey,
                        membership,
                        secondarySelector,
                        shouldPivot,
                        timeScale,
                        translations
                    })}
                    getRowId={(row) => row.id ?? row.name}
                    getTreeDataPath={(row) => row.path}
                    pinnedColumns={{
                        left: [],
                        right: []
                    }}
                    rows={rowsData}
                    slotProps={{
                        root: {
                            disableColumnMenu: true
                        }
                    }}
                    translations={translations}
                    treeData={shouldPivot}
                    {...(shouldPivot && {
                        defaultGroupingExpansionDepth: 0,
                        groupingColDef: {
                            headerName: getNameForHeader(secondarySelector, translations),
                            minWidth: 200,
                            flex: 1,
                            renderCell: (params) => (
                                <GridTreeDataGroupingCell
                                    {...params}
                                    renderer={(cellParams) => cellParams.rowNode.type === 'group' ? cellParams.rowNode.groupingKey : ''}
                                />
                            )
                        }
                    })}
                />
            </div>
            {
                showExportDialog &&
                <LaborReportExportDialog
                    dateRange={selectedTimeframe}
                    onCancel={handleClose}
                    onDownload={handleDownload}
                    rows={[
                        exportContent.header,
                        ...exportContent.rows
                    ]}
                    translations={translations}
                />
            }
        </>
    );
}

WorkboardDataTable.propTypes = {
    dateSelector: PropTypes.string,
    endDate: PropTypes.string,
    isExportPreviewEnabled: PropTypes.bool,
    isMigrated: PropTypes.bool,
    isTimeAxis: PropTypes.bool,
    laborReportData: PropTypes.arrayOf(PropTypes.object),
    membership: PropTypes.membership,
    myJdPermissions: PropTypes.myJdPermissions,
    secondarySelector: PropTypes.string,
    startDate: PropTypes.string,
    timeScale: PropTypes.string,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        isMigrated: state.membership.isMigrated,
        isExportPreviewEnabled: Boolean(state.account.isgToggles?.[PRO_GOLF_LABOR_REPORTS_1]),
        myJdPermissions: state.account.myJdPermissions
    };
}

export default connect(mapStateToProps)(WorkboardDataTable);
